import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, takeUntil } from 'rxjs';

import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-file-upload';
import '@maersk-global/mds-components-core/mc-notification';

import { AuthPolicies } from '../../../auth/auth-policies';
import { PartsManagementService } from '../../../services/parts-management.service';
import { ToastrService } from '../../../services/toastr-service';
import { PartBulkUploadResponse, PartBulkUploadRowError } from '../../../models/part-bulk-upload.model';
import { buildCsv, downloadCsv, generateDateFilename } from '../../../helper/download-helpers';

const ALLOWED_FILE_EXTENSION = 'xlsx';

@Component({
  selector: 'app-parts-management-bulk-upload',
  templateUrl: './parts-management-bulk-upload.component.html',
  styleUrl: './parts-management-bulk-upload.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PartsManagementBulkUploadComponent implements OnDestroy {
  openUploadDialog: boolean = false;
  file: File | undefined;
  uploading: boolean = false;

  /** Client-side rejection of the picked file, shown on the file upload control itself. */
  fileErrorMessage: string | undefined;
  /** File-level errors from the API — the file was rejected outright and nothing was written. */
  fileErrors: string[] = [];
  /** Per-row failures from a file that was processed. */
  rowErrors: PartBulkUploadRowError[] = [];
  errorsTruncated: boolean = false;
  failedCount: number = 0;
  /** Inserted / updated / failed counts, shown when a processed file still had failed rows. */
  resultSummary: string | undefined;

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private readonly authPolicies: AuthPolicies,
    private readonly partsManagementService: PartsManagementService,
    private readonly toastrService: ToastrService,
  ) {}

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get canBulkUpload(): boolean {
    return this.authPolicies.canBulkUploadPartManagement;
  }

  get canSubmit(): boolean {
    return !!this.file && !this.uploading;
  }

  uploadDialog(): void {
    if (!this.canBulkUpload) {
      return;
    }
    this.resetUploadState();
    this.openUploadDialog = true;
  }

  closeUploadDialog(): void {
    this.openUploadDialog = false;
    this.resetUploadState();
  }

  onFileChange(event: Event): void {
    this.clearResults();
    this.fileErrorMessage = undefined;
    const file: File | undefined = (event as CustomEvent).detail?.[0];

    if (!file) {
      this.file = undefined;
      return;
    }

    if (!this.hasAllowedExtension(file.name)) {
      this.file = undefined;
      this.fileErrorMessage = 'Only .xlsx files can be uploaded.';
      return;
    }

    this.file = file;
  }

  /** mc-file-upload rejects anything outside `accept` before it reaches onFileChange. */
  onInvalidFile(): void {
    this.clearResults();
    this.file = undefined;
    this.fileErrorMessage = 'Only .xlsx files can be uploaded.';
  }

  onUploadClick(): void {
    if (!this.canBulkUpload || !this.file || this.uploading) {
      return;
    }

    this.uploading = true;
    this.clearResults();

    this.partsManagementService
      .bulkUploadParts(this.file)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => this.handleResponse(response),
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
  }

  downloadErrorCsv(): void {
    if (this.rowErrors.length === 0) {
      return;
    }

    const rows = this.rowErrors.flatMap((rowError) =>
      (rowError.errors ?? []).map((message) => [rowError.sheetName, rowError.rowNumber, rowError.partCode ?? '', message]),
    );
    downloadCsv(buildCsv(['Sheet', 'Row', 'Part Number', 'Error'], rows), generateDateFilename('part-bulk-upload-errors', 'csv'));
  }

  private handleResponse(response: PartBulkUploadResponse): void {
    this.uploading = false;

    // The API returns the same body on 200 and 400, so fileErrors decides whether anything was written.
    if (response.fileErrors?.length > 0) {
      this.fileErrors = response.fileErrors;
      this.file = undefined;
      return;
    }

    // Rows were written even on a partial success, so the list behind the modal is stale either way.
    this.partsManagementService.triggerPartsRefresh();
    this.file = undefined;

    if (response.failed > 0) {
      this.resultSummary = this.buildSummaryMessage(response);
      this.rowErrors = response.errors ?? [];
      this.errorsTruncated = response.errorsTruncated;
      this.failedCount = response.failed;
      return;
    }

    this.toastrService.showToastr(this.buildSuccessMessage(response), 'success');
    this.openUploadDialog = false;
  }

  private handleError(error: HttpErrorResponse): void {
    this.uploading = false;
    const body = error.error as PartBulkUploadResponse | undefined;

    if (body?.fileErrors?.length) {
      this.fileErrors = body.fileErrors;
      this.file = undefined;
      return;
    }

    // Transport or server failure — keep the file selected so the upload can simply be retried.
    this.toastrService.showToastr('The parts file could not be uploaded. Try again.', 'error');
  }

  private buildSummaryMessage(response: PartBulkUploadResponse): string {
    return `${response.totalRows} row(s) processed — ${response.inserted} added, ${response.updated} updated, ${response.failed} failed.`;
  }

  private buildSuccessMessage(response: PartBulkUploadResponse): string {
    return `${response.totalRows} row(s) processed — ${response.inserted} part(s) added, ${response.updated} part(s) updated.`;
  }

  private hasAllowedExtension(fileName: string): boolean {
    return fileName.split('.').pop()?.toLowerCase() === ALLOWED_FILE_EXTENSION;
  }

  private clearResults(): void {
    this.resultSummary = undefined;
    this.fileErrors = [];
    this.rowErrors = [];
    this.errorsTruncated = false;
    this.failedCount = 0;
  }

  private resetUploadState(): void {
    this.clearResults();
    this.file = undefined;
    this.uploading = false;
    this.fileErrorMessage = undefined;
  }
}
